import { track } from './analytics';
import { SET_LESSON_STARTED, SET_READY_TO_MOVE } from '../store/meeting/actions/actionTypes';
import { SET_SHOW_UNIT_SELECTION_SCREEN } from '../store/course/actionTypes';
import { isQuikbaseContextOnWeb } from '../utils/webRedirects';
import { OPEN_PLANS, SHOW_QUOTA_PAYWALL } from '../store/subscriptionUi/actionTypes';
import { SET_NEW_BADGES } from '../store/relicVault/actionTypes';

/**
 * Player events that the store already knows about (owner 2026-10-13: "mixpanel events all around the UX and user
 * journeys"; Sol mixpanel-coverage D1 A): a lesson starting and ending, the plans opening over the world, a used-up
 * minutes meter and newly earned badges. Watching the actions keeps the screens untouched. It only ever observes: an
 * error here is swallowed and never reaches the product.
 */
// The store's own action names (the badges one is RELIC_VAULT/SET_NEW_BADGES; a copied string missed it on 13 Oct).

const lessonOf = state => {
  const course = state?.course || {};
  return {
    course_id: course.courseData?.curriculumId || null,
    lesson_id: course.currentLessonProgress?.currentLessonId || course.courseData?.currentLessonId || null,
  };
};
const planOf = state => state?.subscriptionUi?.plan?.planKey || null;
const readyOf = state => Boolean(state?.meeting?.meeting?.ready_to_move);
const inQuikbase = state => state?.world?.location === 'quikbase' || isQuikbaseContextOnWeb();

// The funnel's teleport (Sol mixpanel-funnels-2 E A): the unit picked at a portal, kept across the page load the
// teleport makes until that unit's world is ready. Read once, then forgotten; a stale one (over 10 minutes) is ignored.
const TELEPORT_KEY = 'ql-funnel-teleport';
export function noteTeleport(unitId, storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null) {
  try {
    if (unitId) storage?.setItem(TELEPORT_KEY, JSON.stringify({ unitId: String(unitId), at: Date.now() }));
    else storage?.removeItem(TELEPORT_KEY);
  } catch (_) {
    // analytics never breaks the product
  }
}
export function takeTeleport(storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null, now = Date.now()) {
  try {
    const saved = JSON.parse(storage?.getItem(TELEPORT_KEY) || 'null');
    storage?.removeItem(TELEPORT_KEY);
    return saved?.unitId && now - Number(saved.at) < 10 * 60 * 1000 ? saved : null;
  } catch (_) {
    return null;
  }
}
export const meterName = meter => (['voice', 'text', 'course'].includes(meter) ? meter : meter ? 'other' : null);

export function createAnalyticsMiddleware(send = track, now = () => Date.now(), pending = takeTeleport) {
  let lesson = null; // { course_id, lesson_id, startedAt }
  const badgesSent = new Set();
  const endLesson = (after, reason) => {
    if (!lesson) return;
    const progress = Number(after?.course?.currentLessonProgress?.currentLessonProgressPercentage) || 0;
    send('lesson_completed', {
      course_id: lesson.course_id,
      lesson_id: lesson.lesson_id,
      duration_seconds: Math.max(0, Math.round((now() - lesson.startedAt) / 1000)),
      completion_reason: reason || (progress >= 100 ? 'finished' : 'exited'),
    });
    lesson = null;
  };

  const observe = (action, before, after) => {
    switch (action?.type) {
      case SET_LESSON_STARTED: {
        const was = Boolean(before?.meeting?.meeting?.lessonStarted);
        const is = Boolean(after?.meeting?.meeting?.lessonStarted);
        const next = lessonOf(after);
        // another lesson chosen from the menu while one was playing: the first ends, the second starts
        if (was && is && lesson && next.lesson_id && next.lesson_id !== lesson.lesson_id) endLesson(after, 'switched');
        if (is && !lesson) {
          lesson = { ...next, startedAt: now() };
          send('lesson_started', { course_id: lesson.course_id, lesson_id: lesson.lesson_id, entry_point: 'lesson_menu' });
        } else if (was && !is) {
          endLesson(after);
        }
        break;
      }
      case SET_READY_TO_MOVE: {
        if (readyOf(before) || !readyOf(after)) break;
        const course_id = lessonOf(after).course_id;
        if (inQuikbase(after)) {
          pending(); // a teleport that ended back in Quikbase is no unit landing
          send('quikbase_arrived', { course_id });
          break;
        }
        const teleport = pending();
        if (teleport) send('teleport_completed', { course_id, unit_id: teleport.unitId });
        break;
      }
      case SET_SHOW_UNIT_SELECTION_SCREEN: {
        if (action.payload !== true || before?.course?.showUnitSelectionScreen || !inQuikbase(after)) break;
        send('portal_entered', { course_id: lessonOf(after).course_id, subject: after?.course?.curriculumGradeAndSubject?.subject || null });
        break;
      }
      case SHOW_QUOTA_PAYWALL: {
        const kind = action.payload?.kind || 'quota';
        if (kind === 'ended') break; // a session that simply ended is not a limit and opens no plans
        const meter = meterName(action.payload?.meter);
        if (kind === 'quota') send('usage_meter_exhausted', { meter_type: meter, plan_tier: planOf(after) || action.payload?.planKey || null, trigger: meter === 'course' ? 'course_generation' : 'tutor_session' });
        send('player_paywall_viewed', { meter_type: meter, plan_tier: planOf(after), trigger: kind === 'quota' ? 'meter_exhausted' : 'locked_content' });
        break;
      }
      case OPEN_PLANS:
        send('player_paywall_viewed', { meter_type: null, plan_tier: planOf(after), trigger: 'upgrade' });
        break;
      case SET_NEW_BADGES:
        for (const id of Array.isArray(action.payload) ? action.payload : []) {
          if (id == null || badgesSent.has(String(id))) continue;
          badgesSent.add(String(id));
          send('badge_earned', { badge_id: String(id), trigger: 'play' });
        }
        break;
      default:
        break;
    }
  };

  return store => next => action => {
    let before;
    try {
      before = store.getState();
    } catch (_) {
      before = null;
    }
    const result = next(action);
    try {
      observe(action, before, store.getState());
    } catch (_) {
      // analytics never breaks the product
    }
    return result;
  };
}

export default createAnalyticsMiddleware();
